/**
 * Client public-IP resolver — Flutter parity.
 *
 * The .NET backend's `AppointmentManagementService.GetGrpcMetaData()` reads
 * the `IpAddress` HTTP header and passes it as gRPC call metadata.
 * `Grpc.Core.Metadata.Entry` does NOT accept null values — if the header
 * is missing on an HTTP request that triggers the gRPC path (e.g. PUT
 * /api/Doctor/WorkingHour/{id} → UpdateDefaultAppointmentProfile), the
 * controller returns HTTP 500 AFTER the database write has already
 * succeeded. The mutation appears to fail in the UI but the row is
 * actually mutated server-side. Confirmed live 2026-05-20 via direct
 * curl experimentation.
 *
 * Flutter source: `dio_api_client.dart:19-33` calls an Azure HttpTrigger
 * that echoes the caller's public IP; the result is cached on the
 * client and sent as `IpAddress` on every authenticated request
 * (`dio_api_client.dart:367`).
 *
 * We mirror exactly: same Azure endpoint, single-flight cache, fallback
 * to `"0.0.0.0"` if the lookup fails so the header is never absent.
 * The header value isn't load-bearing for routing — the backend just
 * needs a non-null string to pass through to gRPC metadata. Audit logs
 * may show the placeholder when the lookup fails; that's a recognised
 * Flutter-parity tradeoff.
 *
 * Why not use a public service like ipify.org: the Azure endpoint is
 * the codebase convention, matching Flutter keeps audit-log IP shape
 * identical, and we avoid introducing a new external-dependency
 * surface. The endpoint is plaintext-IP, no PHI.
 */

const IP_LOOKUP_URL =
  'https://scrips.azurewebsites.net/api/HttpTrigger1?code=N1Aaqss1Xk9zwPwV9Ti1XlvsE3ccPL63N86ARTS2Nn3cI1IFmOchQg==';

const FALLBACK_IP = '0.0.0.0';

let cachedIp: string | null = null;
let inFlight: Promise<string> | null = null;

async function fetchIpFromAzure(): Promise<string> {
  try {
    const res = await fetch(IP_LOOKUP_URL, { method: 'GET' });
    if (!res.ok) return FALLBACK_IP;
    const text = (await res.text()).trim();
    return text || FALLBACK_IP;
  } catch {
    return FALLBACK_IP;
  }
}

/**
 * Resolve the client's public IP for the `IpAddress` request header.
 *
 * Single-flight: concurrent callers during the first lookup share one
 * fetch. After the first resolution, subsequent calls return the
 * cached value synchronously via the promise resolution.
 *
 * Never rejects — falls back to `"0.0.0.0"` on any failure (network
 * error, non-2xx response, parse failure). This matches Flutter's
 * `getIP()` which catches all exceptions and returns null; we go one
 * step further and substitute a placeholder so the downstream header
 * is always present.
 */
export async function getClientIpAddress(): Promise<string> {
  if (cachedIp !== null) return cachedIp;
  if (!inFlight) {
    inFlight = fetchIpFromAzure().then((ip) => {
      cachedIp = ip;
      inFlight = null;
      return ip;
    });
  }
  return inFlight;
}

/**
 * Test-only cache reset. Calling code never needs this; tests do, so
 * they can exercise the lookup branch independently per case.
 */
export function _resetIpCache(): void {
  cachedIp = null;
  inFlight = null;
}
